import { useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IEvidenceTaskLinkItem } from 'components/AuditCriteria/EvidenceTaskLinks';
import { FormStructureAddControl, IFormStructureAddOption } from 'components/Tasks/Forms/FormStructureAddControl';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapExtraSmall, globalStackTokensGapSmall } from 'globalStyles';
import KPI from 'models/kpi/kpi';
import KPIData from 'models/kpi/kpiData';
import ResourceLink from 'models/resourceLink';
import Task from 'models/tasks/task';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { CSSProperties, Fragment, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SingleFormElement from './SingleFormElement';

interface ITaskTypeFormRendererProps {
  task: Task;
  links: ResourceLink[];
  formElements: TaskTypeFormElement[];
  isLoading: boolean;
  allowEdit: boolean;
  readonly: boolean;
  compact: boolean;
  hideEmptyFormText?: boolean;
  isSystemForm?: boolean;
  onAddAttachment?: () => void;
  onAddContext?: () => void;
  onRemove?: (field: TaskTypeFormElement) => void;
  onEdit?: (field: TaskTypeFormElement) => void;
  onMoveUp?: (field: TaskTypeFormElement) => void;
  onMoveDown?: (field: TaskTypeFormElement) => void;
  onUpdateValue: (kpi: KPI, data: KPIData) => void;
  onUpdateKPI: (kpi: KPI) => void;
  onSetKPI: (kpi: KPI) => void;
  onUpdateTaskForForm: (task: Task) => void;
  addLinks: (links: ResourceLink[]) => void;
  setLinks: (links: ResourceLink[]) => void;
  removeLink: (links: ResourceLink) => void;
  onUpdateField?: (field: TaskTypeFormElement) => void;
  onAddNewKPI?: () => void;
  fieldClassName?: string;
  // The selected field is drawn by SingleFormElementWrapper (hover look plus accent border), so the
  // host adds no class of its own for it.
  isFieldSelected?: (field: TaskTypeFormElement) => boolean;
  renderAddButton?: () => JSX.Element | null;
  renderCommandBar?: () => JSX.Element | null;
  // What to show instead of the plain "this form is empty" line when the form has no fields to
  // render. Modelled on the editing-mode add cards below: centered in the body, not a message bar.
  renderEmptyState?: (noApplicableCriteria?: boolean) => JSX.Element | null;
  // Forwarded to SingleFormElement: the evidence tasks bound to a one-time check, keyed by
  // lower-cased criterionKey, and the handler that opens one.
  evidenceTasksByCriterionKey?: Map<string, IEvidenceTaskLinkItem[]>;
  criterionBadgesByKey?: Map<string, string[]>;
  onOpenEvidenceTask?: (taskId: number) => void;
  // Opens the audit criterion editor for a projected criterion card; forwarded to
  // SingleFormElement. Only criteria-managing hosts pass it.
  onEditCriterion?: (field: TaskTypeFormElement) => void;
  // The names of the entities whose criteria were projected onto this form, keyed
  // `${entityType}|${entityId}`. Present only where the caller knows them (an audit execution
  // task, the procedure form editor): with them the questions are shown entity by entity under a
  // heading, which is the difference between "twelve questions" and "four questions about each of
  // these three items".
  entityNamesByKey?: Map<string, string>;
  // Deleting and reordering a criterion card. Only the criteria-managing form editor passes these.
  onRemoveCriterion?: (field: TaskTypeFormElement) => void;
  onMoveCriterionUp?: (field: TaskTypeFormElement) => void;
  onMoveCriterionDown?: (field: TaskTypeFormElement) => void;
  // Design-time drag & drop: fired when one field card is dropped on another. The editor owns the
  // movement rules (own questions stay inside their own block, criteria reorder within their
  // entity section and persist through the criteria API).
  onDragMove?: (from: TaskTypeFormElement, to: TaskTypeFormElement) => void;
  kpis?: KPI[];
  designTime?: boolean;
  // Render retired (archived) criterion cards even though nobody answered them. Only the criteria
  // editor passes it, driven by its "show archived" toggle.
  showRetired?: boolean;
  // Tighten the vertical rhythm between fields. Set by the design-time forms editor, where the
  // reader scans a long structure rather than filling one field in at a time. The answer-time
  // surfaces (a form on a task, the task-form modal, the criteria list) keep the roomier default.
  denseFields?: boolean;
  /**
   * Let the form flow to its natural height instead of owning a scroll pane. The pane needs a host of
   * a known height (a modal that sets one); inside a panel there is none, and the panel already
   * scrolls its own content area. Same switch, and the same reason, as SharePointPageRenderer's.
   */
  disableScrollContainer?: boolean;
  // Forwarded to SingleFormElement: opens a referenced field's preview when an operand is clicked in
  // a computed field's design-time formula preview.
}

// A RETIRED criterion is history: it stays visible - read-only, see SingleFormElement - while it
// carries the answer this task gave it, and disappears completely when it never got one. Exported
// because "is this form empty?" has to be answered on the SAME list everywhere, or the form renders
// nothing while its owner still thinks it has fields.
//
// The filter belongs here and not in TaskTypeForm: that component's element list is also what
// validateForm walks and what calcKPIExpressions rewrites, and what loadData pushes onto the task as
// KPIData. A retired-but-answered field must stay in all three - dropping it there would take its
// stored answer off the task on the next save. "Do not render" is a rendering rule.
//
// showRetired is for the ONE surface that manages the criteria themselves: there a retired (archived)
// criterion is what the user asked to see, and it has no answer to keep it visible by the rule above.
export function getVisibleFormElements(
  formElements: TaskTypeFormElement[],
  showRetired?: boolean,
): TaskTypeFormElement[] {
  if (showRetired) return formElements;

  // a retired criterion and one outside the audit's standards are hidden alike, unless this task
  // answered them: what was asked and answered stays readable
  return formElements.filter(
    (elm) =>
      (!elm.retiredOn && !elm.notApplicable) ||
      (elm.kpi !== undefined && elm.kpi.data.length > 0 && elm.kpi.data[0].hasAnswer()),
  );
}

const TaskTypeFormRenderer = (props: ITaskTypeFormRendererProps) => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const { t } = useTranslation(['translation', 'forms', 'kpis', 'fieldsLibrary', 'criteria']);

  const systemForm = useMemo(() => {
    if (props.isSystemForm !== undefined) return props.isSystemForm;

    return !!(
      props.task.formVersion?.systemTaskType ??
      appContext.globalDataCache.taskTypes.get(props.task.taskTypeId).systemTaskType
    );
  }, [
    appContext.globalDataCache.taskTypes,
    props.isSystemForm,
    props.task.formVersion?.systemTaskType,
    props.task.taskTypeId,
  ]);

  // KPI badge styling depends on the theme, so memoize it instead of rebuilding per rendered field.
  const badgeStyle = useMemo<CSSProperties>(
    () => ({
      position: 'absolute',
      top: 6,
      right: 6,
      zIndex: 1,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.04em',
      color: theme.palette.white,
      backgroundColor: theme.palette.themePrimary,
      borderRadius: 10,
      padding: '1px 8px',
      transition: 'opacity 120ms ease',
    }),
    [theme],
  );

  // everything below (including the empty state) reads the filtered list, so a form holding nothing
  // but unanswered retired fields counts as empty
  const visibleElements = useMemo(
    () => getVisibleFormElements(props.formElements, props.showRetired),
    [props.formElements, props.showRetired],
  );
  // criteria exist, but none apply under this audit's standards: a different empty state than "no
  // questions yet", and one that must not offer to generate criteria
  const noApplicableCriteria = visibleElements.length === 0 && props.formElements.some((elm) => elm.notApplicable);

  // The criteria of ONE audited item are one thing, not a row of unrelated questions: they are
  // generated together, updated together and belong to that item rather than to this form. So they
  // are drawn as one block - heading, commands and questions inside a single accent border - and the
  // individual cards carry no border of their own. Consecutive-only, which is exactly how the
  // projector writes them.
  const elementGroups = useMemo(() => {
    const groups: { key: string; isCriteria: boolean; items: { elm: TaskTypeFormElement; index: number }[] }[] = [];

    visibleElements.forEach((elm, index) => {
      const isCriterion = !!elm.criterionKey;
      const last = groups[groups.length - 1];
      const sameBlock =
        isCriterion &&
        last?.isCriteria &&
        last.items[0].elm.entityType === elm.entityType &&
        last.items[0].elm.entityId === elm.entityId;

      if (sameBlock) {
        last.items.push({ elm, index });

        return;
      }

      groups.push({
        key: (isCriterion ? 'criteria-' : 'field-') + index.toString(),
        isCriteria: isCriterion,
        items: [{ elm, index }],
      });
    });

    return groups;
  }, [visibleElements]);

  // the card being dragged, by its render key; the drop target resolves it back to the element
  const [draggedKey, setDraggedKey] = useState<string | undefined>(undefined);

  const renderKeyOf = (elm: TaskTypeFormElement): string =>
    elm.taskTypeId.toString() + '-' + elm.fieldType.toString() + '-' + (elm.kpiId?.toString() ?? '');

  const onDropOn = (targetKey: string, target: TaskTypeFormElement) => {
    const from =
      draggedKey !== undefined && draggedKey !== targetKey
        ? visibleElements.find((item) => renderKeyOf(item) === draggedKey)
        : undefined;
    setDraggedKey(undefined);
    if (from) props.onDragMove?.(from, target);
  };

  const defaultAddOptions: IFormStructureAddOption[] = [
    {
      key: 'field',
      icon: 'Add',
      label: t('fieldsLibrary:Detail.NewField'),
      onClick: () => {
        if (props.onAddNewKPI) props.onAddNewKPI();
      },
      disabled: !props.onAddNewKPI,
      title: t('forms:Dialogs.EditForm.AddNew.NewKPIInfo'),
    },
    {
      key: 'attachment',
      icon: 'Attach',
      label: t('forms:Dialogs.EditForm.AddNew.NewAttachment'),
      onClick: () => props.onAddAttachment?.(),
      disabled: systemForm || !props.onAddAttachment,
      title: systemForm
        ? t('forms:Dialogs.EditForm.AddNew.NewAttachmentInfoDisabled')
        : t('forms:Dialogs.EditForm.AddNew.NewAttachmentInfo'),
    },
    {
      key: 'context',
      icon: 'Link',
      label: t('forms:Dialogs.EditForm.AddNew.NewContext'),
      onClick: () => props.onAddContext?.(),
      disabled: systemForm || !props.onAddContext,
      title: systemForm
        ? t('forms:Dialogs.EditForm.AddNew.NewContextInfoDisabled')
        : t('forms:Dialogs.EditForm.AddNew.NewContextInfo'),
    },
  ];

  const defaultAddControl = () => (
    <FormStructureAddControl options={defaultAddOptions} variant={visibleElements.length === 0 ? 'cards' : 'menu'} />
  );

  if (props.isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPStack.Item grow>
          <IPSpinner size={IPSpinnerSize.large} />
        </IPStack.Item>
      </IPStack>
    );
  }

  // The heading a field opens a new section with: the entity its criterion belongs to, and only
  // where the caller could name it. The fields of one entity already sit together (the projector
  // writes them that way), so a heading is needed exactly where the entity CHANGES - and never for
  // a form of one entity, which would only repeat what the task itself says.
  //
  // A field with no entity is not a criterion of the last one heading the list - it is a question
  // somebody added to this task - so it opens a section of its own instead of reading as the
  // previous entity's. Same for an entity the caller could not name: heading it with the one above
  // it would say something untrue about which item it audits.
  const entityKeyOf = (elm: TaskTypeFormElement): string | undefined =>
    elm.entityType !== undefined && elm.entityId !== undefined ? `${elm.entityType}|${elm.entityId}` : undefined;

  const criteriaManagementActive = !!props.designTime && !!props.onEditCriterion;

  const getSectionHeader = (elm: TaskTypeFormElement, index: number): string | undefined => {
    if (!props.entityNamesByKey || (props.entityNamesByKey.size < 2 && !criteriaManagementActive)) return undefined;

    const previous = index > 0 ? visibleElements[index - 1] : undefined;
    if (previous !== undefined && entityKeyOf(previous) === entityKeyOf(elm)) return undefined;

    const key = entityKeyOf(elm);
    if (!key) {
      //the first field of the form needs no heading - nothing precedes it to be confused with
      return index === 0 ? undefined : t('forms:View.OwnQuestions');
    }

    return props.entityNamesByKey.get(key);
  };

  const criteriaGroupStyles = {
    root: {
      margin: '1px',
      border: `1px solid ${theme.semanticColors.accentButtonBackground}`,
      borderRadius: 3,
      padding: '6px 8px',
    },
  };

  const renderElement = (elm: TaskTypeFormElement): JSX.Element => {
    const fieldSelected = props.isFieldSelected?.(elm) ?? false;

    const elementKey = elm.taskTypeId.toString() + '-' + elm.fieldType.toString() + '-' + (elm.kpiId?.toString() ?? '');
    const draggable = !!props.designTime && !!props.onDragMove;

    return (
      <Fragment key={elementKey}>
        <IPStack.Item
          className={props.fieldClassName}
          styles={{ root: { position: 'relative', selectors: { ':hover .fl-kpi-badge': { opacity: 0 } } } }}
        >
          {/* the drag handlers live on a plain div: Stack.Item does not forward DOM props */}
          <div
            draggable={draggable}
            onDragStart={draggable ? () => setDraggedKey(elementKey) : undefined}
            onDragOver={draggable ? (event) => event.preventDefault() : undefined}
            onDragEnd={draggable ? () => setDraggedKey(undefined) : undefined}
            onDrop={draggable ? () => onDropOn(elementKey, elm) : undefined}
          >
            {props.designTime && elm.kpi && !elm.kpi.isField && (
              <span className="fl-kpi-badge" style={badgeStyle}>
                {t('kpis:Entity.KPI')}
              </span>
            )}
            <SingleFormElement
              field={elm}
              task={props.task}
              links={props.links}
              compact={props.compact}
              readonly={props.readonly}
              onUpdateValue={props.onUpdateValue}
              onUpdateKPI={props.onUpdateKPI}
              onSetKPI={props.onSetKPI}
              onUpdateTaskForForm={props.onUpdateTaskForForm}
              addLinks={props.addLinks}
              setLinks={props.setLinks}
              removeLink={props.removeLink}
              onUpdateField={props.onUpdateField}
              allowEdit={props.allowEdit}
              onEdit={props.onEdit}
              onRemove={props.onRemove}
              onMoveUp={props.onMoveUp}
              onMoveDown={props.onMoveDown}
              kpis={props.kpis}
              designTime={props.designTime}
              selected={fieldSelected}
              evidenceTasksByCriterionKey={props.evidenceTasksByCriterionKey}
              criterionBadgesByKey={props.criterionBadgesByKey}
              onOpenEvidenceTask={props.onOpenEvidenceTask}
              onEditCriterion={props.onEditCriterion}
              onRemoveCriterion={props.onRemoveCriterion}
              onMoveCriterionUp={props.onMoveCriterionUp}
              onMoveCriterionDown={props.onMoveCriterionDown}
            />
          </div>
        </IPStack.Item>
      </Fragment>
    );
  };

  const fieldGapTokens = props.denseFields ? globalStackTokensGapExtraSmall : globalStackTokensGapSmall;

  const formContent = (
    <IPStack tokens={fieldGapTokens}>
      {elementGroups.map((group) => {
        const first = group.items[0];
        const sectionHeader = getSectionHeader(first.elm, first.index);

        const body = (
          <Fragment>
            {sectionHeader && (
              <IPStack.Item
                styles={{
                  root: {
                    paddingTop: first.index === 0 || group.isCriteria ? 0 : props.denseFields ? 8 : 12,
                  },
                }}
              >
                <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
                  <IPStack.Item>
                    <IPLabel>{sectionHeader}</IPLabel>
                  </IPStack.Item>
                </IPStack>
              </IPStack.Item>
            )}
            {group.items.map((item) => renderElement(item.elm))}
          </Fragment>
        );

        if (!group.isCriteria) {
          return <Fragment key={group.key}>{body}</Fragment>;
        }

        return (
          <IPStack.Item key={group.key} styles={criteriaGroupStyles}>
            <IPStack tokens={fieldGapTokens}>{body}</IPStack>
          </IPStack.Item>
        );
      })}
      {/* A caller that knows WHY the form is empty says so itself, with the ways out of it. Centered
          in the body like the editing-mode add cards below. */}
      {visibleElements.length === 0 && !props.allowEdit && !props.hideEmptyFormText && props.renderEmptyState && (
        <IPStack horizontalAlign="center" verticalAlign="center" styles={{ root: { paddingTop: 20 } }}>
          {props.renderEmptyState(noApplicableCriteria)}
        </IPStack>
      )}
      {visibleElements.length === 0 && !props.hideEmptyFormText && !props.allowEdit && !props.renderEmptyState && (
        <IPStack horizontalAlign="center" verticalAlign="center" styles={{ root: { paddingTop: 20 } }}>
          <IPText>{t('forms:Dialogs.EditForm.Empty')}</IPText>
        </IPStack>
      )}
      {/* Empty form (editing): the onboarding "add" cards live in the body, centered. Once the form has
          fields, the compact "+ Add" menu button takes over at the top instead (see the return below). */}
      {props.allowEdit && visibleElements.length === 0 && (
        <IPStack horizontalAlign="center" styles={{ root: { paddingTop: 24 } }}>
          {props.renderAddButton ? props.renderAddButton() : defaultAddControl()}
        </IPStack>
      )}
    </IPStack>
  );

  return (
    <IPStack verticalFill={!props.disableScrollContainer} tokens={globalStackTokensGapSmall}>
      {props.renderCommandBar && <IPStack.Item>{props.renderCommandBar()}</IPStack.Item>}
      {/* On a form that already has fields, the compact "+ Add" menu button is pinned at the top so it
          stays visible and never gets clipped. (Empty forms show the onboarding cards in the body.) */}
      {props.allowEdit && visibleElements.length > 0 && !props.renderCommandBar && (
        <IPStack.Item styles={{ root: { borderBottom: `1px solid ${theme.palette.neutralLight}`, paddingBottom: 4 } }}>
          {props.renderAddButton ? props.renderAddButton() : defaultAddControl()}
        </IPStack.Item>
      )}
      <ScrollableStackItem minHeight={100} disableScroll={props.disableScrollContainer === true}>
        {formContent}
      </ScrollableStackItem>
    </IPStack>
  );
};

export default TaskTypeFormRenderer;
